import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_FIRMWARE_LIST,
	GET_FIRMWARE_MANAGEMENT_LIST,
	GET_SWITCH_FIRMWARE_LIST,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_FALLBACK_LIMIT,
	LOOKUP_LIMIT,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {resolveByFields} from '../../core/fieldSearch';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * The Switch API names a row's models as an array and the Access Point API as
 * one string. The field catalogue reads a stored value as text and skips
 * anything that is not, so an array of models was never matched - "the ES1G
 * firmware" found nothing on a row that plainly listed it.
 * @param {*} models stored model attribute
 * @return {string|null} models as one string
 */
function modelsText(models) {
	if (Array.isArray(models)) {
		return models.map((model) => optionalText(model)).filter(Boolean).join(', ') ||
			null;
	}
	return optionalText(models);
}

/**
 * Describe a firmware image by what a person calls it.
 *
 * Firmware belongs to a device model, so "12.3.50.6 for the ion4xi" is how a
 * row is named - and the same version can sit on several rows, one per model,
 * which is why the model rides along instead of the file name alone.
 * @param {object} row Canvas image row
 * @return {string} display name
 */
function imageName(row) {
	const version = optionalText(row?.firmware_version);
	const models = modelsText(row?.device_type ?? row?.device_models);
	if (version && models) return `${version} (${models})`;
	return version || optionalText(row?.file) || '';
}

/**
 * Describe a scheduled upgrade task. Its own name leads: that is the column a
 * person reads off the list, and the version alone repeats across tasks.
 * @param {object} row Canvas task row
 * @return {string} display name
 */
function taskName(row) {
	return optionalText(row?.schedule_name) || imageName(row);
}

/**
 * The text that finds one row again in its own list's search box.
 * @param {string} kind entity kind
 * @param {object} row Canvas row
 * @return {string} search text
 */
export function searchTextForRow(kind, row) {
	if (kind === 'ap_firmware_task') return taskName(row);
	return optionalText(row?.firmware_version) || optionalText(row?.file) || '';
}

// What each list's last lookup showed, by display name, so a name the agent
// resolved can be turned back into text the list's search box understands.
const namedRows = new Map();

const KINDS = {
	ap_firmware: {
		path: GET_FIRMWARE_LIST,
		serverSearch: true,
		rowId: (row) => row?.firmware_id,
		name: imageName,
	},
	ap_firmware_task: {
		path: GET_FIRMWARE_MANAGEMENT_LIST,
		serverSearch: true,
		rowId: (row) => row?.scheduled_id,
		name: taskName,
	},
	switch_firmware: {
		path: GET_SWITCH_FIRMWARE_LIST,
		// The Switch list has no search of its own to ask, so it is read whole
		// and ranked here - it holds a handful of images, not thousands.
		serverSearch: false,
		rowId: (row) => row?.firmware_id,
		name: imageName,
	},
};

/**
 * Copy a Canvas row so the catalogue's attributes can read every model.
 * @param {string} kind entity kind
 * @param {object} item Canvas row
 * @return {object} row the generic lookup can read
 */
function rowForSearch(kind, item) {
	const models = modelsText(item.device_models);
	return {
		...item,
		...(kind === 'switch_firmware'
			? {device_models: models, device_type: models}
			: {device_type: modelsText(item.device_type)}),
	};
}

/**
 * Normalize a Canvas row to what the agent reasons about.
 * @param {string} kind entity kind
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
function normalizeRow(kind, item) {
	const config = KINDS[kind];
	const id = config.rowId(item);
	const name = config.name(item);
	if (id == null || !name) return null;
	return {
		entity_type: kind,
		entity_id: String(id),
		name,
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Load rows from one list, paging until the lookup cap.
 * @param {string} kind entity kind
 * @param {string|null} search text to search the list for
 * @param {number} limit maximum rows
 * @return {Promise<Array>} rows
 */
async function loadRows(kind, search, limit) {
	const config = KINDS[kind];
	const rows = [];
	const pageSize = Math.min(Math.max(limit, 1), LOOKUP_LIMIT);
	let offset = 0;
	while (rows.length < limit) {
		const searchParam = search && config.serverSearch
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${config.path}` +
			`?limit=${pageSize}&offset=${offset}${searchParam}`;
		const {data} = await axios.get(url);
		const page = responseRows(decodeAgentResponse(data) || {});
		rows.push(...page);
		if (page.length < pageSize) break;
		offset += pageSize;
	}
	return rows.slice(0, limit);
}

/**
 * Rank loaded rows through the catalogue the agent sent.
 * @param {string} kind entity kind
 * @param {Array} rows Canvas rows
 * @param {object} params resolver parameters
 * @param {string} query lookup query
 * @param {number} limit maximum candidates
 * @param {boolean} [fuzzy] allow near-miss matches
 * @return {Array} ranked candidates
 */
function candidatesFromRows(kind, rows, params, query, limit, fuzzy = false) {
	return resolveByFields({
		rows: rows.map((row) => rowForSearch(kind, row)),
		normalizeRow: (row) => normalizeRow(kind, row),
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
		fallbackValues: (candidate) => [candidate?.name],
		fuzzy,
	});
}

// Words that say what kind of thing a firmware row is, not which one. The
// request "delete firmware 12.3.50.6" reaches the lookup as the query
// "firmware 12.3.50.6", and no row's version, model or file contains that
// sentence - so the one row that plainly matched was reported not found.
const KIND_WORDS = new Set([
	'firmware', 'firmwares', 'image', 'images', 'task', 'tasks', 'upgrade',
	'scheduled', 'schedule', 'ap', 'aps', 'access', 'point', 'points', 'switch',
	'switches', 'the', 'a', 'an', 'for', 'version', 'model',
]);

/**
 * Drop the words that only name the kind of row, keeping what identifies it.
 * Left whole when nothing else would remain, so "the firmware" still lists.
 * @param {string} query lookup query
 * @return {string} query reduced to its identifying words
 */
export function identifyingWords(query) {
	const words = String(query || '').trim().split(/\s+/).filter(Boolean);
	const kept = words.filter((word) => !KIND_WORDS.has(word.toLocaleLowerCase()));
	return kept.length ? kept.join(' ') : words.join(' ');
}

/**
 * Build the resolver for one list.
 * @param {string} kind entity kind
 * @return {Function} resolver
 */
function resolverFor(kind) {
	return async function resolveFirmware(params) {
		const query = identifyingWords(params.query);
		const browse = params.browse === true;
		if (!query && !browse) throw new Error('lookup_query_required');
		if (!query) {
			const limit = Math.min(
				Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
				LOOKUP_LIMIT,
			);
			const rows = await loadRows(kind, null, limit);
			retain(kind, rows);
			return {candidates: candidatesFromRows(kind, rows, params, '', limit)};
		}
		let rows = await loadRows(kind, query, LOOKUP_LIMIT);
		let candidates = candidatesFromRows(kind, rows, params, query, LOOKUP_LIMIT);
		if (!candidates.length) {
			// The list's own search matches some columns and not others, so an
			// empty answer is not proof there is no such row. Reading more of
			// the list and ranking it here is how a model name is found on a
			// list whose search only looks at versions.
			rows = await loadRows(kind, null, LOOKUP_FALLBACK_LIMIT);
			candidates = candidatesFromRows(
				kind, rows, params, query, LOOKUP_LIMIT,
			);
			if (!candidates.length && params.fuzzy === true) {
				candidates = candidatesFromRows(
					kind, rows, params, query, LOOKUP_LIMIT, true,
				);
			}
		}
		retain(kind, rows);
		return {candidates};
	};
}

export const resolveApFirmware = resolverFor('ap_firmware');
export const resolveApFirmwareTask = resolverFor('ap_firmware_task');
export const resolveSwitchFirmware = resolverFor('switch_firmware');

/**
 * Remember a lookup's rows, by id for focusing and by name for filtering.
 * @param {string} kind entity kind
 * @param {Array} rows Canvas rows
 * @return {void}
 */
function retain(kind, rows) {
	const config = KINDS[kind];
	replaceResolvedEntities(kind, rows, config.rowId);
	namedRows.set(kind, new Map(rows.map((row) => [config.name(row), row])));
}

/**
 * Return a retained row from the last lookup.
 * @param {string} kind entity kind
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedFirmware(kind, entityId) {
	return getResolvedEntity(kind, entityId);
}

/**
 * Turn what the agent shows for a row back into what the list can search for.
 *
 * The agent filters by the name it displayed - "12.3.50.6 (ion4xi)" - because
 * that is the name it resolved. The list's own search box matches the columns,
 * not that sentence, so typing it in whole narrows the list to nothing and
 * the row being pointed at disappears. A name that was resolved is mapped back
 * to its row; anything else is a search the person typed and is left alone.
 *
 * @param {string} kind entity kind
 * @param {string} query text the agent wants shown in the box
 * @return {string} text for the box
 */
export function searchTextForQuery(kind, query) {
	const text = String(query || '').trim();
	const row = namedRows.get(kind)?.get(text);
	return row ? searchTextForRow(kind, row) : text;
}
